import _typeof from "@babel/runtime-corejs3/helpers/esm/typeof";
import _asyncToGenerator from "@babel/runtime-corejs3/helpers/esm/asyncToGenerator";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";
import _Array$from from "@babel/runtime-corejs3/core-js-stable/array/from";
import _startsWithInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/starts-with";
import _regeneratorRuntime from "@babel/runtime-corejs3/regenerator";
// RelayOS KiwiIRC - Inline Entry Point

import Vue from 'vue';
import _ from 'lodash';
import JSON5 from 'json5';
import Logger from '@/libs/Logger';
import ConfigLoader from '@/libs/ConfigLoader';
import getState from '@/libs/state';
import ThemeManager from '@/libs/ThemeManager';
import GlobalApi from '@/libs/GlobalApi';
import * as Misc from '@/helpers/Misc';
import { loadHostConfig } from '@/utils/host-config-loader';
import { loadRenderingModeCSS } from '@/utils/rendering-mode-loader';

// Import inline components
import InlineContainer from '@/components/inline/InlineContainer';
import MessageList from '@/components/MessageList';
import ControlInput from '@/components/ControlInput';
import StateBrowser from '@/components/StateBrowser';

// Import global styles
import 'font-awesome/css/font-awesome.css';

// Set up logging
var logLevelMatch = window.location.href.match(/kiwi-loglevel=(\d)/);
if (logLevelMatch && logLevelMatch[1]) {
  var newLevel = parseInt(logLevelMatch[1], 10);
  Logger.setLevel(newLevel);
  Logger('Logging level set to', newLevel);
}
var log = Logger.namespace('inline');

// Create the global API
var api = window.kiwi = GlobalApi.singleton();

// Set the rendering mode in the global API
api.setRenderingMode('inline');

/**
 * RelayOS KiwiIRC Inline Chat
 *
 * This class provides the API for embedding the RelayOS KiwiIRC inline chat in any website.
 */
var RelayOSInline = /*#__PURE__*/function () {
  /**
   * Create a new RelayOS KiwiIRC inline chat
   * @param {Object} options - Inline chat configuration options
   */
  function RelayOSInline() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    _classCallCheck(this, RelayOSInline);
    this.options = {
      title: options.title || 'RelayOS IRC',
      container: options.container || null,
      server: options.server || null,
      channel: options.channel || null,
      theme: options.theme || null,
      showSettings: options.showSettings !== undefined ? options.showSettings : true,
      showChannelList: options.showChannelList !== undefined ? options.showChannelList : true,
      onConnect: options.onConnect || null,
      onDisconnect: options.onDisconnect || null,
      onMessage: options.onMessage || null
    };
    this.app = null;
    this.state = getState();
    this.initialized = false;

    // Initialize the inline chat
    this.init();
  }

  /**
   * Initialize the inline chat
   * @private
   */
  return _createClass(RelayOSInline, [{
    key: "init",
    value: (function () {
      var _init = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var _t;
        return _regeneratorRuntime.wrap(function (_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _context.prev = 0;
              _context.next = 1;
              return this.loadConfig();
            case 1:
              // Create the container
              this.createContainer();

              // Initialize the theme
              this.initTheme();

              // Mount the Vue app
              this.mountApp();

              // Connect to the IRC server
              if (this.options.server) {
                this.connect();
              }
              this.initialized = true;
              log.info('Inline chat initialized');
              _context.next = 3;
              break;
            case 2:
              _context.prev = 2;
              _t = _context["catch"](0);
              log.error('Error initializing inline chat:', _t);
            case 3:
            case "end":
              return _context.stop();
          }
        }, _callee, this, [[0, 2]]);
      }));
      function init() {
        return _init.apply(this, arguments);
      }
      return init;
    }()
    /**
     * Load the inline chat configuration
     * @private
     */
    )
  }, {
    key: "loadConfig",
    value: (function () {
      var _loadConfig = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        var configFile, configObj, configLoader, config, _t2;
        return _regeneratorRuntime.wrap(function (_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              configFile = 'static/inline-config.json';
              configObj = null; // Check for a meta tag with configuration
              if (document.querySelector('meta[name="kiwiconfig"]')) {
                configFile = document.querySelector('meta[name="kiwiconfig"]').content;
              }

              // Check for a script tag with configuration
              if (document.querySelector('script[name="kiwiconfig"]')) {
                try {
                  configObj = JSON5.parse(document.querySelector('script[name="kiwiconfig"]').innerHTML);
                } catch (error) {
                  log.error('Error parsing config from script tag:', error);
                }
              }

              // Create a config loader
              configLoader = new ConfigLoader();
              configLoader.addValueReplacement('protocol', window.location.protocol).addValueReplacement('wsprotocol', window.location.protocol === 'https:' ? 'wss:' : 'ws:').addValueReplacement('tls', window.location.protocol === 'https:').addValueReplacement('hostname', window.location.hostname).addValueReplacement('host', window.location.host).addValueReplacement('port', window.location.port || (window.location.protocol === 'https:' ? 443 : 80)).addValueReplacement('hash', (window.location.hash || '').substr(1)).addValueReplacement('query', (window.location.search || '').substr(1)).addValueReplacement('referrer', window.document.referrer);

              // Load the configuration
              if (!configObj) {
                _context2.next = 2;
                break;
              }
              _context2.next = 1;
              return configLoader.loadFromObj(configObj);
            case 1:
              _t2 = _context2.sent;
              _context2.next = 4;
              break;
            case 2:
              _context2.next = 3;
              return configLoader.loadFromUrl(configFile);
            case 3:
              _t2 = _context2.sent;
            case 4:
              config = _t2;
              _context2.next = 5;
              return loadHostConfig(config);
            case 5:
              config = _context2.sent;
              // Apply the configuration to the state
              Misc.dedotObject(config);
              this.applyConfig(config);

              // Override with options passed to the constructor
              if (this.options.server) {
                this.state.settings.startupOptions.server = this.options.server;
              }
              if (this.options.channel) {
                this.state.settings.startupOptions.channel = this.options.channel;
              }
              if (this.options.theme) {
                this.state.settings.theme = this.options.theme;
              }
            case 6:
            case "end":
              return _context2.stop();
          }
        }, _callee2, this);
      }));
      function loadConfig() {
        return _loadConfig.apply(this, arguments);
      }
      return loadConfig;
    }()
    /**
     * Apply the configuration to the state
     * @param {Object} config - The configuration object
     * @private
     */
    )
  }, {
    key: "applyConfig",
    value: function applyConfig(config) {
      // Recursively merge an object onto another via Vue.$set
      function applyConfigObj(obj, target) {
        _.each(obj, function (val, key) {
          if (_typeof(val) === 'object') {
            if (_typeof(target[key]) !== 'object') {
              Vue.set(target, key, _.isArray(val) ? [] : {});
            }
            applyConfigObj(val, target[key]);
          } else {
            Vue.set(target, key, val);
          }
        });
      }
      applyConfigObj(config, this.state.settings);
    }

    /**
     * Create the container
     * @private
     */
  }, {
    key: "createContainer",
    value: function createContainer() {
      // If a container is specified, use it
      if (this.options.container) {
        if (typeof this.options.container === 'string') {
          this.container = document.querySelector(this.options.container);
        } else {
          this.container = this.options.container;
        }
        if (!this.container) {
          throw new Error("Container not found: ".concat(this.options.container));
        }
      } else {
        // Create a new container
        this.container = document.createElement('div');
        this.container.id = 'relayos-inline-container';
        document.body.appendChild(this.container);
      }
    }

    /**
     * Initialize the theme
     * @private
     */
  }, {
    key: "initTheme",
    value: (function () {
      var _initTheme = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        var themeMgr, _t3;
        return _regeneratorRuntime.wrap(function (_context3) {
          while (1) switch (_context3.prev = _context3.next) {
            case 0:
              themeMgr = ThemeManager.instance(this.state);
              api.setThemeManager(themeMgr);

              // Load the rendering mode-specific CSS
              _context3.prev = 1;
              _context3.next = 2;
              return loadRenderingModeCSS();
            case 2:
              _context3.next = 4;
              break;
            case 3:
              _context3.prev = 3;
              _t3 = _context3["catch"](1);
              log.error('Failed to load rendering mode CSS:', _t3);
            case 4:
            case "end":
              return _context3.stop();
          }
        }, _callee3, this, [[1, 3]]);
      }));
      function initTheme() {
        return _initTheme.apply(this, arguments);
      }
      return initTheme;
    }()
    /**
     * Mount the Vue app
     * @private
     */
    )
  }, {
    key: "mountApp",
    value: function mountApp() {
      var self = this;

      // Create the Vue app
      this.app = new Vue({
        el: this.container,
        data: function data() {
          return {
            title: self.options.title,
            showSettings: self.options.showSettings,
            showChannelList: self.options.showChannelList
          };
        },
        computed: {
          $state: function $state() {
            return getState();
          }
        },
        methods: {
          onToggleSettings: function onToggleSettings(isOpen) {
            log.info('Settings toggled:', isOpen);
          }
        },
        render: function render(h) {
          return h(InlineContainer, {
            props: {
              title: this.title,
              showSettings: this.showSettings
            },
            on: {
              'toggle-settings': this.onToggleSettings
            }
          }, [h('div', {
            class: 'kiwi-inline-layout'
          }, [this.showChannelList ? h('div', {
            class: 'kiwi-inline-sidebar'
          }, [h(StateBrowser)]) : null, h('div', {
            class: 'kiwi-inline-main'
          }, [h(MessageList), h(ControlInput)])])]);
        }
      });

      // Make the app instance available via the API
      api.setVueInstance(this.app);

      // Add inline-specific styles
      this.addInlineStyles();
    }

    /**
     * Add inline-specific styles
     * @private
     */
  }, {
    key: "addInlineStyles",
    value: function addInlineStyles() {
      var style = document.createElement('style');
      style.textContent = "\n            .kiwi-inline-layout {\n                display: flex;\n                height: 100%;\n                width: 100%;\n            }\n\n            .kiwi-inline-sidebar {\n                width: 200px;\n                border-right: 1px solid #e0e0e0;\n                overflow-y: auto;\n            }\n\n            .kiwi-inline-main {\n                flex: 1;\n                display: flex;\n                flex-direction: column;\n                overflow: hidden;\n            }\n        ";
      document.head.appendChild(style);
    }

    /**
     * Connect to the IRC server
     * @public
     */
  }, {
    key: "connect",
    value: function connect() {
      if (!this.initialized) {
        log.warn('Inline chat not initialized yet, connection deferred');
        return;
      }

      // TODO: Implement connection logic
      log.info('Connecting to IRC server');
      if (this.options.onConnect) {
        this.options.onConnect();
      }
    }

    /**
     * Disconnect from the IRC server
     * @public
     */
  }, {
    key: "disconnect",
    value: function disconnect() {
      if (!this.initialized) {
        log.warn('Inline chat not initialized yet');
        return;
      }

      // TODO: Implement disconnection logic
      log.info('Disconnecting from IRC server');
      if (this.options.onDisconnect) {
        this.options.onDisconnect();
      }
    }

    /**
     * Send a message to the current channel
     * @param {string} message - The message to send
     * @public
     */
  }, {
    key: "sendMessage",
    value: function sendMessage(message) {
      if (!this.initialized) {
        log.warn('Inline chat not initialized yet');
        return;
      }

      // TODO: Implement message sending logic
      log.info('Sending message:', message);
    }

    /**
     * Join a channel
     * @param {string} channel - The channel to join
     * @public
     */
  }, {
    key: "joinChannel",
    value: function joinChannel(channel) {
      if (!this.initialized) {
        log.warn('Inline chat not initialized yet');
        return;
      }

      // TODO: Implement channel joining logic
      log.info('Joining channel:', channel);
    }

    /**
     * Leave a channel
     * @param {string} channel - The channel to leave
     * @public
     */
  }, {
    key: "leaveChannel",
    value: function leaveChannel(channel) {
      if (!this.initialized) {
        log.warn('Inline chat not initialized yet');
        return;
      }

      // TODO: Implement channel leaving logic
      log.info('Leaving channel:', channel);
    }

    /**
     * Set the theme
     * @param {string} theme - The theme name
     * @public
     */
  }, {
    key: "setTheme",
    value: function setTheme(theme) {
      if (!this.initialized) {
        log.warn('Inline chat not initialized yet');
        return;
      }
      api.getThemeManager().setTheme(theme);
    }

    /**
     * Get the current theme
     * @returns {string} - The current theme name
     * @public
     */
  }, {
    key: "getTheme",
    value: function getTheme() {
      if (!this.initialized) {
        log.warn('Inline chat not initialized yet');
        return null;
      }
      return api.getThemeManager().getCurrentTheme();
    }

    /**
     * Get the available themes
     * @returns {Array} - The available themes
     * @public
     */
  }, {
    key: "getAvailableThemes",
    value: function getAvailableThemes() {
      if (!this.initialized) {
        log.warn('Inline chat not initialized yet');
        return [];
      }
      return api.getThemeManager().getAvailableThemes();
    }
  }]);
}(); // Create a global RelayOSInline constructor
window.RelayOSInline = RelayOSInline;

// Auto-initialize if the data-auto-init attribute is present
document.addEventListener('DOMContentLoaded', function () {
  var scriptTag = document.querySelector('script[data-relayos-inline]');
  if (scriptTag && scriptTag.getAttribute('data-auto-init') !== 'false') {
    // Parse options from data attributes
    var options = {};
    _Array$from(scriptTag.attributes).forEach(function (attr) {
      var _context4;
      if (_startsWithInstanceProperty(_context4 = attr.name).call(_context4, 'data-')) {
        var optionName = attr.name.replace('data-', '').replace(/-([a-z])/g, function (g) {
          return g[1].toUpperCase();
        });
        if (optionName !== 'relayosInline' && optionName !== 'autoInit') {
          options[optionName] = attr.value;
        }
      }
    });

    // Create the inline chat
    return new RelayOSInline(options);
  }
  return null;
});
export default RelayOSInline;